import { FC } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { Controller, Form, FormProvider } from 'react-hook-form';
import { t } from 'i18next';

import {
  PracticeSearchFormField,
  ProviderSearchFormField,
  SelectFormField,
  TextFormField,
  UserSearchFormField,
} from 'components/DynamicFormFields';
import { useEditCareTeam } from '@views/pages/Patient/components/CareTeam/components/hooks';
import { CustomModal, SaveChangesModal, StatusBanner } from '@components';
import { theme } from '@store';
import { isNil } from 'lodash';

interface EditCareTeamProps {
  patient_id: string;
  disabled: boolean;
}

export const EditCareTeam: FC<EditCareTeamProps> = ({
  patient_id,
  disabled = false,
}) => {
  const {
    isOpen,
    handleClose,
    handleOpen,
    isSubmitting,
    formMethods,
    onSubmit,
    partnerOptions,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
    modalHeight,
    hasCanvasId,
  } = useEditCareTeam(patient_id);

  const { control, setValue } = formMethods;
  const { getValues } = formMethods;

  const selectedPartner = getValues('partner');
  const providerType = getValues('provider_type');

  return (
    <>
      <Button disabled={disabled} sx={{ height: '30px' }} onClick={handleOpen}>
        {t('patient.careTeam.edit')}
      </Button>

      <CustomModal
        isOpen={isOpen && !disabled}
        onClose={handleClose}
        title={t('patient.careTeam.modal.editCareTeam.title')}
        height={modalHeight}
      >
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {hasCanvasId && (
              <StatusBanner
                text={t('patient.careTeam.modal.canvasWarning')}
                variant={'lightWarning'}
              />
            )}

            <SelectFormField
              name='partner'
              label={t('patient.careTeam.modal.labels.partner')}
              options={partnerOptions}
              onChangeCallback={() => {
                setValue('practice', '');
              }}
              disabled={hasCanvasId}
            />

            <PracticeSearchFormField
              name='practice'
              placeholder={t('patient.careTeam.modal.labels.practice')}
              record_id={selectedPartner}
              disabled={hasCanvasId}
            />

            <Typography variant='subtitle2'>
              {t('patient.careTeam.modal.labels.PrimaryCarePhysician')}
            </Typography>

            <Controller
              name={'provider_type'}
              control={control}
              disabled={hasCanvasId}
              render={({ field, fieldState: { error } }) => (
                <FormControl sx={{ width: '100%' }}>
                  <RadioGroup {...field} row>
                    <FormControlLabel
                      key={'Internal option'}
                      value={'Internal'}
                      control={<Radio value={'Internal'} />}
                      label={t('patients.addModal.provider_type.internal')}
                      disabled={hasCanvasId}
                    />
                    <FormControlLabel
                      key={'External option'}
                      value={'External'}
                      control={<Radio value={'External'} />}
                      label={t('patients.addModal.provider_type.external')}
                      disabled={hasCanvasId}
                    />
                    <FormControlLabel
                      key={'Unknown option'}
                      value={'Unknown'}
                      control={<Radio value={'Unknown'} />}
                      label={t('patients.addModal.provider_type.unknown')}
                      disabled={hasCanvasId}
                    />
                  </RadioGroup>
                  <FormHelperText error={!isNil(error)}>
                    {error ? error.message : null}
                  </FormHelperText>
                </FormControl>
              )}
            />
            {providerType === 'Internal' && (
              <ProviderSearchFormField
                name='provider'
                placeholder={t('patient.careTeam.modal.labels.internalPCP')}
                disabled={hasCanvasId}
              />
            )}
            {providerType === 'External' && (
              <>
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: 'row',
                    gap: '0.5rem',
                  }}
                >
                  <TextFormField
                    name='provider_first_name'
                    label={t('patient.careTeam.modal.labels.providerFirstName')}
                    disabled={hasCanvasId}
                  />

                  <TextFormField
                    name='provider_last_name'
                    label={t('patient.careTeam.modal.labels.providerLastName')}
                    disabled={hasCanvasId}
                  />
                </Box>
                <TextFormField
                  name='provider_phone'
                  label={t('patient.careTeam.modal.labels.providerPhone')}
                  disabled={hasCanvasId}
                />
              </>
            )}

            <Typography variant='subtitle2' sx={{ mt: 1 }}>
              {t('patient.careTeam.otherProviders')}
            </Typography>

            <ProviderSearchFormField
              name='geropsych'
              placeholder={t('patient.careTeam.modal.labels.geropsych')}
              optional
              disabled={hasCanvasId}
            />

            <ProviderSearchFormField
              name='cardiology'
              placeholder={t('patient.careTeam.modal.labels.cardiology')}
              optional
              disabled={hasCanvasId}
            />

            <ProviderSearchFormField
              name='palliativeCare'
              placeholder={t('patient.careTeam.modal.labels.palliativeCare')}
              optional
              disabled={hasCanvasId}
            />

            <ProviderSearchFormField
              name='neurology'
              placeholder={t('patient.careTeam.modal.labels.neurology')}
              optional
              disabled={hasCanvasId}
            />

            <ProviderSearchFormField
              name='home_health_provider_id'
              placeholder={t(
                'patient.careTeam.modal.labels.home_health_provider_id',
              )}
              optional
              disabled={hasCanvasId}
            />

            <ProviderSearchFormField
              name='socialWorker'
              placeholder={t('patient.careTeam.modal.labels.socialWorker')}
              optional
              disabled={hasCanvasId}
            />

            <UserSearchFormField
              name='careNavigator'
              placeholder={t('patient.careTeam.modal.labels.careNavigator')}
              optional
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                position: 'sticky',
                bottom: 0,
                paddingY: 3,
                backgroundColor: theme.palette.common.white,
                zIndex: 3,
              }}
            >
              <Button
                variant='text'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                }}
                disabled={isSubmitting}
                onClick={handleClose}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='secondary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                disabled={isSubmitting || isEditSubmitting}
                loading={isEditSubmitting}
                loadingPosition='start'
              >
                {t(`general.${isEditSubmitting ? 'saving' : 'save'}`)}
              </Button>
            </Box>
            <SaveChangesModal
              isOpen={isSaveChangesModalOpen}
              onClose={handleCloseSaveChangesModal}
              isValid={isValid}
              handleSaveChanges={handleSaveChanges}
              discardChanges={handleDiscardChanges}
              isLoading={isEditSubmitting}
            />

            {/* <ConfirmationModal
              title={t('patient.careTeam.modal.confirmPCPChange.title')}
              message={t('patient.careTeam.modal.confirmPCPChange.description')}
              onConfirm={submitForm}
              onClose={handleCloseConfirmPCPModal}
              isOpen={isConfirmPCPChangeOpen}
              confirmText={t(
                'patient.careTeam.modal.confirmPCPChange.confirmationText',
              )}
            /> */}
          </Form>
        </FormProvider>
      </CustomModal>
    </>
  );
};
